import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { MessagingService, SendMessageParams, reportConversation } from "@/services/messagingService";
import { Message, ConversationWithUser } from "@/types/messages";
import { UserProfile } from "@/types/user";
import { MessageError } from "@/types/messages";
import { MessageErrorHandler } from "@/utils/messageErrorHandler";
import { useToast } from "@/hooks/use-toast";

interface MessagingConfig {
  currentUser: UserProfile | null;
  messagingService: MessagingService;
}

interface UseMessagingReturn {
  // Conversations
  conversations: ConversationWithUser[];
  isLoadingConversations: boolean;
  conversationsError: Error | null;

  // Current conversation
  currentConversation: string | null;
  messages: Message[];
  isLoadingMessages: boolean;
  messagesError: Error | null;
  hasMoreMessages: boolean;

  // Actions
  sendMessage: (content: string) => Promise<void>;
  startConversation: (otherUserId: string) => Promise<string>;
  setCurrentConversation: (conversationId: string | null) => void;
  deleteConversation: (conversationId: string) => Promise<void>;
  loadMoreMessages: () => Promise<void>;

  // Status
  unreadCount: Record<string, number>;
  isLoadingUnread: boolean;

  // Error handling
  messageErrors: Record<string, MessageError>;
  clearMessageError: (conversationId: string) => void;
  retryMessage: (conversationId: string, content: string) => Promise<void>;

  // Pending conversations
  pendingConversations: ConversationWithUser[];
  handleReportConversation: (conversationId: string, reportedUserId: string) => Promise<void>;
}

export const useMessaging = ({ currentUser, messagingService }: MessagingConfig): UseMessagingReturn => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [currentConversation, setCurrentConversationState] = useState<string | null>(null);
  const [otherUserId, setOtherUserId] = useState<string | null>(null);
  const [messageErrors, setMessageErrors] = useState<Record<string, MessageError>>({});
  const MESSAGES_PER_PAGE = 25;

  // Create startConversation mutation first since it's used in the callback
  const startConversationMutation = useMutation({
    mutationFn: async (otherUserId: string) => {
      const talentId = currentUser?.type === "talent" ? currentUser.id : otherUserId;
      const promoterId = currentUser?.type === "promoter" ? currentUser.id : otherUserId;
      return messagingService.startConversation(talentId, promoterId);
    },
    onSuccess: (conversationId) => {
      queryClient.invalidateQueries({
        queryKey: ["conversations", currentUser?.id],
      });
      return conversationId;
    },
  });

  // Fetch conversations
  const {
    data: conversations = [],
    isLoading: isLoadingConversations,
    error: conversationsError,
  } = useQuery({
    queryKey: ["conversations", currentUser?.id],
    queryFn: () => {
      if (!currentUser?.id || !currentUser?.type) return [];
      return messagingService.getConversations(currentUser.id, currentUser.type);
    },
    enabled: !!currentUser?.id,
    staleTime: 30000,
  });

  // Fetch messages with infinite query - now using subcollection path
  const {
    data: messagesData,
    isLoading: isLoadingMessages,
    error: messagesError,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ["messages", currentConversation],
    queryFn: async ({ pageParam }: { pageParam: string | null }) => {
      if (!currentConversation) {
        return {
          messages: [],
          nextCursor: null,
          hasMore: false
        };
      }
      const result = await messagingService.getMessages(
        currentConversation,
        MESSAGES_PER_PAGE,
        pageParam
      );

      // Preserve the original timestamps and ensure correct sender IDs
      const messagesWithCorrectData = result.messages.map(msg => ({
        ...msg,
        senderId: msg.senderId,
        // Ensure we use the original timestamp from the database
        timestamp: msg.timestamp || Date.now(),
        type: msg.type || 'text'
      }));

      // Sort messages by timestamp in ascending order (oldest first)
      const sortedMessages = messagesWithCorrectData.sort((a, b) => 
        (a.timestamp || 0) - (b.timestamp || 0)
      );

      return {
        ...result,
        messages: sortedMessages,
        hasMore: result.hasMore,
        nextCursor: result.nextCursor
      };
    },
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextCursor : undefined,
    enabled: !!currentConversation,
    initialPageParam: null,
    staleTime: 5000,
  });

  // Flatten messages from all pages
  const messages = messagesData?.pages.flatMap(page => page.messages) ?? [];

  // Get unread counts with optimized querying
  const {
    data: unreadCount = {},
    isLoading: isLoadingUnread
  } = useQuery({
    queryKey: ["unreadCount", currentUser?.id],
    queryFn: () => {
      if (!currentUser?.id) return {};
      return messagingService.getUnreadCount(currentUser.id);
    },
    enabled: !!currentUser?.id,
    refetchInterval: 30000,
    staleTime: 15000,
  });

  // Optimized mutations
  const sendMessageMutation = useMutation({
    mutationFn: async ({
      content,
      conversationId,
      recipientId,
      senderId,
      senderType
    }: SendMessageParams) => {
      if (!senderId || !senderType) throw new Error("User data missing");
      return messagingService.sendMessage({
        content,
        conversationId,
        recipientId,
        senderId,
        senderType
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ["messages", result.conversationId]
      });
      queryClient.invalidateQueries({
        queryKey: ["conversations", currentUser?.id]
      });
    },
    onError: (error: any) => {
      if (currentConversation) {
        const messageError = MessageErrorHandler.handleMessageError(error);
        setMessageErrors((prev) => ({
          ...prev,
          [currentConversation]: messageError,
        }));
      }
    },
  });

  // Optimized delete mutation
  const deleteConversationMutation = useMutation({
    mutationFn: (conversationId: string) => {
      if (!currentUser?.id) throw new Error("User not found");
      return messagingService.deleteConversation(conversationId, currentUser.id);
    },
    onSuccess: (_, conversationId) => {
      // Remove conversation from cache
      queryClient.setQueryData(
        ["conversations", currentUser?.id],
        (old: ConversationWithUser[] | undefined) =>
          old?.filter(conv => conv.id !== conversationId) ?? []
      );
      
      // Remove messages from cache
      queryClient.removeQueries({
        queryKey: ["messages", conversationId]
      });

      // Reset current conversation if it was deleted
      if (currentConversation === conversationId) {
        setCurrentConversationState(null);
      }
    },
  });

  // Mark messages as read when conversation is opened
  useEffect(() => {
    if (currentConversation && currentUser?.id) {
      messagingService
        .markMessagesAsRead(currentConversation, currentUser.id)
        .then(() => {
          // Update local cache to show messages as read
          queryClient.setQueryData(
            ["messages", currentConversation],
            (old: any) => {
              if (!old?.pages) return old;
              return {
                ...old,
                pages: old.pages.map((page: any) => ({
                  ...page,
                  messages: page.messages.map((msg: Message) => ({
                    ...msg,
                    read: true
                  }))
                }))
              };
            }
          );
        })
        .catch((error) => {
          // Silently handle permission errors
          console.warn('Could not mark messages as read:', error);
        });
    }
  }, [currentConversation, currentUser?.id]);

  // Handler functions
  const sendMessage = async (content: string) => {
    if (!currentUser || !content.trim() || !currentConversation || !otherUserId) return;

    const currentTimestamp = Date.now();
    const newMessage = {
      id: currentTimestamp.toString(), // temporary ID
      content: content.trim(),
      senderId: currentUser.id,
      timestamp: currentTimestamp, // Use the same timestamp throughout
      type: 'text' as const,
      read: false,
    };

    // Optimistically update the messages in the cache
    queryClient.setQueryData(
      ['messages', currentConversation],
      (old: any) => {
        if (!old?.pages) return old;
        
        // Add new message to the end of the first page
        const updatedFirstPage = {
          ...old.pages[0],
          messages: [...old.pages[0].messages, newMessage].sort((a, b) => 
            (a.timestamp || 0) - (b.timestamp || 0)
          )
        };

        return {
          ...old,
          pages: [updatedFirstPage, ...old.pages.slice(1)]
        };
      }
    );

    try {
      // Send the message without timestamp
      await messagingService.sendMessage({
        content: content.trim(),
        conversationId: currentConversation,
        recipientId: otherUserId,
        senderId: currentUser.id,
        senderType: currentUser.type
      });

      // Only invalidate conversations to update the last message
      queryClient.invalidateQueries({
        queryKey: ['conversations', currentUser.id]
      });
      
    } catch (error) {
      console.error('Failed to send message:', error);
      // Revert the optimistic update on error
      queryClient.invalidateQueries({
        queryKey: ['messages', currentConversation]
      });
      
      setMessageErrors(prev => ({
        ...prev,
        [currentConversation]: {
          message: 'Failed to send message. Please try again.',
          retryable: true
        }
      }));
    }
  };

  const startConversation = useCallback(
    async (otherUserId: string) => {
      const result = await startConversationMutation.mutateAsync(otherUserId);
      return result;
    },
    [startConversationMutation]
  );

  const deleteConversation = useCallback(
    async (conversationId: string) => {
      await deleteConversationMutation.mutateAsync(conversationId);
    },
    [deleteConversationMutation]
  );

  const loadMoreMessages = useCallback(async () => {
    if (hasNextPage) {
      await fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  const clearMessageError = useCallback((conversationId: string) => {
    setMessageErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[conversationId];
      return newErrors;
    });
  }, []);

  const retryMessage = useCallback(
    async (conversationId: string, content: string) => {
      if (
        messageErrors[conversationId] &&
        MessageErrorHandler.isRetryableError(messageErrors[conversationId])
      ) {
        await sendMessage(content);
      }
    },
    [messageErrors, sendMessage]
  );

  const setCurrentConversation = useCallback((conversationId: string | null) => {
    setCurrentConversationState(conversationId);
    const conversation = conversations.find(c => c.id === conversationId);
    setOtherUserId(conversation?.otherUser.id ?? null);
  }, [conversations]);

  const handleReportConversation = async (conversationId: string, reportedUserId: string) => {
    try {
      await reportConversation(conversationId, reportedUserId);
      toast({
        title: "Report Submitted",
        description: "Thank you for your report. We will review it shortly.",
      });
    } catch (error) {
      console.error('Error reporting conversation:', error);
      toast({
        title: "Error",
        description: "Failed to submit report. Please try again.",
        variant: "destructive",
      });
    }
  };

  return {
    conversations,
    isLoadingConversations,
    conversationsError,
    currentConversation,
    messages,
    isLoadingMessages,
    messagesError,
    hasMoreMessages: !!hasNextPage,
    sendMessage,
    startConversation,
    setCurrentConversation,
    deleteConversation,
    loadMoreMessages,
    unreadCount,
    isLoadingUnread,
    messageErrors,
    clearMessageError,
    retryMessage,
    pendingConversations: [],
    handleReportConversation,
  };
}